import { useState, useEffect } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePromptConfigs, useSavePromptConfig } from "@/hooks/api/usePromptConfigs";

type PromptConfig = {
  key: string;
  content: string | null;
  updatedAt: string | null;
};

function PromptCard({ config }: { config: PromptConfig }) {
  const { t } = useTranslation();
  const [content, setContent] = useState(config.content ?? "");

  useEffect(() => {
    setContent(config.content ?? "");
  }, [config.content]);

  const mutation = useSavePromptConfig();

  const isDirty = content !== (config.content ?? "");

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          {t(`prompts.${config.key}.title`, { defaultValue: config.key })}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t(`prompts.${config.key}.description`, { defaultValue: "" })}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={10}
          className="font-mono text-xs"
          placeholder={t("prompts.placeholder")}
        />
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            disabled={!isDirty || !content.trim() || mutation.isPending}
            onClick={() => mutation.mutate({ key: config.key, content: content.trim() })}
          >
            {mutation.isPending ? t("prompts.saving") : t("common.save")}
          </Button>
          {config.updatedAt && (
            <span className="text-xs text-muted-foreground">
              {t("prompts.lastSaved")} {new Date(config.updatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PromptManager() {
  const { t } = useTranslation();

  const { data: configs, isLoading } = usePromptConfigs();

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t("prompts.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("prompts.subtitle")}</p>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="space-y-6 max-w-3xl">
          {configs?.map((config) => (
            <PromptCard key={config.key} config={config} />
          ))}
        </div>
      )}
    </Layout>
  );
}
